import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import SkeletonLoader from "../../../utilites/SkeltonLoader";
import BreadCrumb from "../../../pages/breadcrumb/BreadCrumb";
import FormGeneratorPermission from "./form-generator-permssion";
import { fetchSections } from "../../form-builder/slices/FbSections";
import { fetchAssignedPermission, saveSectionPermission } from "../slice/FbAssignedPermission";
import { setSelectedRolesFormatted } from "../slice/FbAssignedPermission";

const AssignSectionPermission = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { id, assignedId } = useParams();
    const BreadCrumbData = location.state || [];

    const [sections, setSection] = useState([]);

    const { assignedSectionPermission, isLoading } = useSelector(
        (state) => state.assignedTemplates
    );

    //  Need allRoles to rebuild selectedRolesFormatted after refetch
    const { allRoles } = useSelector((state) => state?.bmrRoles);


const { selectedRolesFormatted } = useSelector(
    (state) => state.assignedTemplates
);

//  Set of currently valid role_ids from selectedRolesFormatted
const validRoleIds = useMemo(
    () => new Set(selectedRolesFormatted?.map((r) => r.value) || []),
    [selectedRolesFormatted]
);

// ── Merge saved permissions into fetched sections ──────────────────────
const sectionsWithSavedPermissions = useMemo(() => {
    if (!sections?.length) return [];

    const savedSections =
        assignedSectionPermission?.question_schema?.sections || [];

    return sections.map((section) => {
        const saved = savedSections.find(
            (s) => s.section_uuid === section.section_uuid
        );

        // Filter saved permissions — only keep roles still in selectedRolesFormatted
        const filteredView = (saved?.persmissions?.view ?? []).filter(
            (p) => validRoleIds.has(p.role_id)
        );
        const filteredFill = (saved?.persmissions?.fill ?? []).filter(
            (p) => validRoleIds.has(p.role_id)
        );
        return {
            ...section,
            persmissions: {
                view: filteredView,
                fill: filteredFill,
            },
        };
    });
}, [sections, assignedSectionPermission, validRoleIds]);

    const formData = useMemo(
        () => ({ sections: sectionsWithSavedPermissions }),
        [sectionsWithSavedPermissions]
    );

    // ── Fetch sections ─────────────────────────────────────────────────────
    const handleFetchSection = async () => {
        try {
            const response = await dispatch(fetchSections(id ?? 5));
            setSection(response?.payload?.data?.sections);
        } catch (error) {
            console.error("Error", error);
        }
    };

    //  Helper — rebuild selectedRolesFormatted from saved section permissions
    const rebuildRolesFormatted = (savedSections) => {
        const uniqueRoleIds = new Set();
        savedSections?.forEach((section) => {
            section?.persmissions?.view?.forEach((p) => uniqueRoleIds.add(p.role_id));
            section?.persmissions?.fill?.forEach((p) => uniqueRoleIds.add(p.role_id));
        });

        return Array.from(uniqueRoleIds).map((roleId) => ({
            value: roleId,
            label: allRoles?.find((r) => r.id === roleId)?.title || String(roleId),
        }));
    };

    useEffect(() => {
        handleFetchSection();
         dispatch(fetchAssignedPermission({ assignedId }))
    }, []);

    // ── Submit ─────────────────────────────────────────────────────────────
    const PORTAL = 2;

    const toSlug = (name) =>
        name
            ?.toLowerCase()
            .trim()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, "");

    const onSubmit = async (sectionsPayload) => {
        const sectionsWithPermissions = sectionsWithSavedPermissions.map((section) => {
            const permData = sectionsPayload.find(
                (p) => p.section_uuid === section.section_uuid
            );

            const viewRoleIds = Array.isArray(permData?.view?.role_id)
                ? permData.view.role_id : [];
            const fillRoleIds = Array.isArray(permData?.fill?.role_id)
                ? permData.fill.role_id : [];

            const slug = toSlug(section.name);

            return {
                ...section,
                persmissions: {
                    view: viewRoleIds.map((role_id) => ({ role_id, slug, portal: PORTAL })),
                    fill: fillRoleIds.map((role_id) => ({ role_id, slug, portal: PORTAL })),
                },
            };
        });
        const payload = {
            question_schema: { sections: sectionsWithPermissions },
        };
        try {
            await dispatch(
                saveSectionPermission({ assignedId, question_schema: payload.question_schema })
            ).unwrap();

            // Re-fetch after successful save
            const refetchAction = await dispatch(fetchAssignedPermission({ assignedId }));
            const savedSections = refetchAction?.payload?.question_schema?.sections || [];

            //  Rebuild selectedRolesFormatted so chips show correct labels
            if (savedSections.length) {
                const rolesFormatted = rebuildRolesFormatted(savedSections);
                dispatch(setSelectedRolesFormatted(rolesFormatted));
            }

        } catch (error) {
            console.error("Failed to save permissions →", error);
        }
    };

    return (
        <div className="!p-6">
            {isLoading ? (
                <SkeletonLoader rowCount={1} isTable={false} width="100%" height={100} />
            ) : (
                <>
                    {BreadCrumbData?.length > 0 && (
                        <div className="mb-4">
                            <BreadCrumb BreadCrumbsListing={BreadCrumbData} />
                        </div>
                    )}
                    <FormGeneratorPermission
                        formJson={sectionsWithSavedPermissions}
                        isSectionsPermission={true}
                        loading={false}
                        formData={formData}
                        onSubmit={onSubmit}
                    />
                </>
            )}
        </div>
    );
};

export default AssignSectionPermission;